import { CssBaseline, ThemeProvider } from '@mui/material';
import { ConfirmOptions, ConfirmProvider } from 'material-ui-confirm';
import { isTablet } from 'react-device-detect';
import App from './App';

import { Auth0Provider } from '@auth0/auth0-react';
import { I18nWrapper } from '@chargeafter/wrappers';
import { makeStyles } from '@mui/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { SnackbarProvider } from 'notistack';
import { ErrorBoundary } from 'react-error-boundary';
import { IntlProvider } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import GeneralError from './components/GeneralError';
import { TrackerWrapper } from './components/TrackerWrapper';
import { NotFound } from './pages/404';
import { Login } from './pages/Login';
import { VerticalModeBlocker } from './pages/VerticalModeBlocker';
import './sentry';
import {
  useGetAuth0Data,
  useGetMerchantId,
  useGetMerchantTheme,
  useSetupMerchantEnvironment,
} from './services/configManager';
import { useStartOver } from './services/flowManager';
import logger from './services/loggerManager';
import { useSetShowError } from './services/userManager';
import { environment } from './environment';

const useStyles = makeStyles({
  snackbarRoot: {
    left: '0 !important',
    bottom: '0 !important',
    '& .MuiCollapse-wrapper': {
      paddingBottom: '0 !important',
    },
  },
});

const confirmOptions: ConfirmOptions = {
  allowClose: false,
  confirmationButtonProps: {
    fullWidth: true,
    variant: 'contained',
    disableElevation: true,
    sx: {
      borderRadius: 0,
    },
  },
  cancellationButtonProps: {
    fullWidth: true,
    variant: 'outlinedSecondary',
    sx: {
      borderRadius: 0,
    },
    disableElevation: true,
  },
  dialogActionsProps: {
    sx: {
      padding: 0,
      display: 'flex',
      button: {
        margin: '0 !important',
        height: '48px',
      },
    },
  },
  dialogProps: {
    PaperProps: {
      accessKey: 'confirmation-modal',
    },
    maxWidth: 'sm',
    fullWidth: false,
  },
  titleProps: {
    height: '64px',
    textTransform: 'uppercase',
    variant: 'h6',
    alignContent: 'center',
    sx: {
      backgroundColor: 'secondary.light',
      color: 'primary.main',
      padding: '0 !important',
    },
  },
};

const onBoundaryErrorHandler = (e: Error, info: { componentStack: string }) => {
  logger.error(
    `Error boundary exception. error: ${JSON.stringify(
      e,
    )}, info: ${JSON.stringify(info.componentStack)}`,
    {
      filename: 'index.tsx',
      function: 'AppRoot',
    },
  );
};

const AppRoot = () => {
  useSetupMerchantEnvironment();
  const navigate = useNavigate();
  const classes = useStyles();
  const startOver = useStartOver();
  const merchantId = useGetMerchantId();
  const { clientId, organizationId } = useGetAuth0Data();
  const muiMerchantTheme = useGetMerchantTheme();
  const resetShowError = useSetShowError();
  const hostName = window.location.hostname.split('.')[0]?.toLowerCase();

  if (hostName !== 'authenticate' && merchantId === '') {
    return (
      <ThemeProvider theme={muiMerchantTheme}>
        <CssBaseline />
        <NotFound />
      </ThemeProvider>
    );
  }

  const params = new URLSearchParams(window.location.search);
  const invitation = params.get('invitation') || undefined;
  return (
    <>
      <Auth0Provider
        domain={environment.auth0Domain!}
        clientId={clientId!}
        organization={organizationId!}
        redirectUri={window.location.origin + '/main'}
        audience="https://api.chargeafter.com"
        invitation={invitation}
        useRefreshTokens
      >
        <ThemeProvider theme={muiMerchantTheme}>
          <LocalizationProvider
            {...{ dateAdapter: AdapterDateFns }}
            localeText={{ start: 'From', end: 'To' }}
          >
            <TrackerWrapper>
              {isTablet && <VerticalModeBlocker />}
              <IntlProvider locale={'en-US'}>
                <CssBaseline />
                <ErrorBoundary
                  FallbackComponent={GeneralError}
                  onError={onBoundaryErrorHandler}
                  onReset={() => {
                    startOver();
                    resetShowError(false);
                    navigate('/');
                  }}
                >
                  <ConfirmProvider defaultOptions={confirmOptions}>
                    <SnackbarProvider
                      maxSnack={1}
                      classes={{
                        containerRoot: classes.snackbarRoot,
                      }}
                    >
                      <I18nWrapper
                        messages={undefined}
                        locale={'en-US'}
                        defaultLocale={'en-US'}
                      >
                        {hostName !== 'authenticate' ? <App /> : <Login />}
                      </I18nWrapper>
                    </SnackbarProvider>
                  </ConfirmProvider>
                </ErrorBoundary>
              </IntlProvider>
            </TrackerWrapper>
          </LocalizationProvider>
        </ThemeProvider>
      </Auth0Provider>
    </>
  );
};

export default AppRoot;
